import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, Routes, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatomoRouteData } from 'ngx-matomo-client';

import { authCanActivateChildFn, authCanActivateFn, publicCanActivateFn } from '@webapp/shared';

import { playerCanMatchFn } from './player/guards';

/**
 * The composition root, and the only file that sees both experiences.
 *
 * Everything below the two authenticated branches belongs to exactly one of them: `./coach` and
 * `./player` own their own route trees, shells and data loading, and neither imports the other.
 * The branches are mutually exclusive by `playerCanMatchFn`, so a signed-in user is only ever
 * inside one of them.
 */
export const APP_ROUTES: Routes = [
  /** Public routes */
  {
    path: 'login',
    canActivate: [publicCanActivateFn],
    loadComponent: () => import('./auth').then((m) => m.LoginPage),
    data: { matomo: { title: 'Login' } as MatomoRouteData },
  },
  {
    path: 'signup',
    canActivate: [publicCanActivateFn],
    loadComponent: () => import('./auth').then((m) => m.SignupPage),
    data: { matomo: { title: 'Signup' } as MatomoRouteData },
  },
  {
    path: 'forgot-password',
    canActivate: [publicCanActivateFn],
    loadComponent: () => import('./auth').then((m) => m.ForgotPasswordPage),
    data: { matomo: { title: 'Forgot Password' } as MatomoRouteData },
  },
  {
    path: 'new-password',
    canActivate: [authCanActivateFn],
    loadComponent: () => import('./auth').then((m) => m.NewPasswordPage),
    data: { matomo: { title: 'New Password' } as MatomoRouteData },
  },
  {
    path: 'verify-account',
    canActivate: [publicCanActivateFn],
    loadComponent: () => import('./auth').then((m) => m.AccountVerificationPage),
    data: { matomo: { title: 'Account Verification' } as MatomoRouteData },
  },
  /** Onboarding */
  {
    path: 'create-organization',
    canActivate: [authCanActivateFn],
    loadComponent: () => import('./auth').then((m) => m.CreateTenantPage),
    data: { matomo: { title: 'Register Organization' } as MatomoRouteData },
  },
  /** Shared reports — a public link, rendered without a session */
  {
    path: 'shared/:token',
    loadComponent: () => import('./coach/features/reports').then((m) => m.SharedReportPage),
  },
  /**
   * Authenticated routes, player experience.
   *
   * Evaluated before the coach branch below. `playerCanMatchFn` matches only player users; for
   * every other user it returns false and Angular falls through to the coach branch.
   */
  {
    path: '',
    canMatch: [playerCanMatchFn],
    canActivateChild: [authCanActivateChildFn],
    loadChildren: () => import('./player'),
  },
  /** Authenticated routes, coach experience */
  {
    path: '',
    canActivateChild: [authCanActivateChildFn],
    loadChildren: () => import('./coach/routes'),
  },
];

@Injectable()
export class TranslatedTitleStrategy extends TitleStrategy {
  /**
   * The default page title should match the `<title>` set in the `index.html`
   * to stay consistent.
   */
  private readonly DEFAULT_PAGE_TITLE = 'KINEXON Sports Cloud App';

  private readonly title = inject(Title);
  private readonly translate = inject(TranslateService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const title = this.buildTitle(snapshot);

    if (typeof title === 'undefined') {
      return this.title.setTitle(this.DEFAULT_PAGE_TITLE);
    }

    this.translate.get(title).subscribe((translatedTitle) => {
      this.title.setTitle(`${translatedTitle} - ${this.DEFAULT_PAGE_TITLE}`);
    });
  }
}
